import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { TIMEZONES } from '../../config/Constants';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';
import { Role } from '../../types';
import { PermissionsEditor } from '../common/PermissionsEditor';
import { UserPermissions, fetchDefaultPermissionsFromBackend, getAssignableRoles } from '../../config/permissions';

// Role descriptions for user invitation/editing
const ROLE_DESCRIPTIONS: Record<string, string> = {
  headCoach: 'Complete admin permissions.',
  assistantCoach: 'Most admin permissions. No editing stats after they lock.',
  statManager: 'Most admin permissions. No editing stats after they lock. No access to KPIs.',
  statTaker: 'Only permissions related to capturing game data.',
};

interface AddUserModalProps {
  onClose: () => void;
  onUserAdded?: () => void;
  editUser?: any | null;
  showClientSelection?: boolean; // Whether to show client selection dropdown
  defaultClientId?: string; // Default client ID for non-admin users
}

// Zod validation schema
const createUserSchema = (showClientSelection: boolean) =>
  z
    .object({
      email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      phone: z.string().min(1, 'Phone number is required'),
      timezone: z.string().min(1, 'Timezone is required'),
      active: z.boolean(),
      clientId: z.string().optional(),
      permissions: z
        .object({
          editUsers: z.boolean(),
          editKpis: z.boolean(),
          editPlayers: z.boolean(),
          editOpponents: z.boolean(),
          editSeasons: z.boolean(),
          editGames: z.boolean(),
        })
        .optional(),
      clientRoles: z.array(z.string()).optional(),
      employeeRole: z.string().optional(),
    })

    .refine(
      (data) => {
        // If this is a client user, they must have exactly one client role
        if (data.clientId && (!data.clientRoles || data.clientRoles.length === 0)) {
          return false;
        }
        // If this is an employee user, they must have an employee role
        if (!data.clientId && (!data.employeeRole || data.employeeRole === '')) {
          return false;
        }
        return true;
      },
      {
        message: 'Please select a role for this user',
        path: ['clientRoles'], // Default to client roles, will adjust based on user type
      },
    );

const AddUserModal: React.FC<AddUserModalProps> = ({
  onClose,
  onUserAdded,
  editUser,
  showClientSelection = true, // Default to true for backward compatibility
  defaultClientId, // Default client ID for non-admin users
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [customPermissions, setCustomPermissions] = useState<Partial<UserPermissions> | null>(null);
  const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);
  const [backendDefaultPermissions, setBackendDefaultPermissions] = useState<Record<string, UserPermissions> | null>(
    null,
  );
  const [currentUserPermissions, setCurrentUserPermissions] = useState<Partial<UserPermissions> | null>(null);

  // Check if current user can manage permissions
  // Head Coach, Assistant Coach, Employees, OR users with full users permission (AED)
  const isHeadCoach = sessionManager.isHeadCoach();
  const isAssistantCoach = sessionManager.isAssistantCoach();
  const isEmployee = sessionManager.isEmployee();
  const hasFullUserPermissions =
    sessionManager.canPerformAction('users', 'add') &&
    sessionManager.canPerformAction('users', 'edit') &&
    sessionManager.canPerformAction('users', 'delete');
  const canManagePermissions = isHeadCoach || isAssistantCoach || isEmployee || hasFullUserPermissions;

  // Check if the user being edited is a Head Coach
  const isEditingHeadCoach = useMemo(() => {
    if (!editUser || !editUser.clientRoles || editUser.clientRoles.length === 0) {
      return false;
    }
    const editUserRole = roles.find((r) => r._id === editUser.clientRoles[0]);
    return editUserRole?.value === 'headCoach';
  }, [editUser, roles]);

  // Only Head Coaches and Employees can edit Head Coach permissions
  const canEditThisUser = useMemo(() => {
    if (!editUser) return true; // Can create new users
    if (isEmployee) return true; // Employees can edit anyone
    if (isHeadCoach) return true; // Head Coaches can edit anyone
    if (isEditingHeadCoach) return false; // Assistant Coaches and below cannot edit Head Coaches
    return true; // Can edit non-Head Coaches
  }, [editUser, isEmployee, isHeadCoach, isEditingHeadCoach]);

  // Get assignable roles based on current user's role and permissions
  const assignableRoles = useMemo(() => {
    if (isEmployee) {
      // Employees can assign any client role
      return roles.filter((role) => role.type === 'client');
    }

    const currentUserRole = sessionManager.getClientRole();
    if (!currentUserRole || !currentUserRole.value) {
      // If no role found, return all roles (fallback)
      return roles.filter((role) => role.type === 'client');
    }

    // If user has full user management permissions via custom permissions,
    // they can assign roles up to their own level
    // Otherwise, use the standard hierarchy
    const assignableRoleValues = getAssignableRoles(currentUserRole.value);

    return roles.filter((role) => role.type === 'client' && assignableRoleValues.includes(role.value));
  }, [roles, isEmployee]);

  // Create schema based on client selection visibility
  const userSchema = useMemo(() => createUserSchema(showClientSelection), [showClientSelection]);
  type UserFormData = z.infer<typeof userSchema>;

  // Initialize react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control,
    reset,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      timezone: 'alaska',
      active: true,
      clientId: '',
      permissions: {
        editUsers: false,
        editKpis: false,
        editPlayers: false,
        editOpponents: false,
        editSeasons: false,
        editGames: false,
      },
      clientRoles: [], // Array of role IDs for clients
      employeeRole: '', // Single role ID for employees
    },
  });

  // Watch clientId to determine user type
  const clientId = watch('clientId');
  const watchedClientRoles = watch('clientRoles');
  const isClient = !!clientId;

  // Get user roles to determine permissions
  const canManageEmployees = sessionManager.canManageEmployees();

  // Fetch clients and roles on component mount
  useEffect(() => {
    fetchClients();
    fetchRoles();
    fetchDefaultPermissions(); // Fetch from backend
    fetchCurrentUserPermissions(); // Load current user's permissions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch current user's permissions to limit what they can assign
  const fetchCurrentUserPermissions = () => {
    const currentUser = sessionManager.getUser();

    // Head Coaches and Employees have full permissions - no restrictions
    if (isHeadCoach || isEmployee) {
      setCurrentUserPermissions(null);
      return;
    }

    // Use the user's FULL permissions (from session), not just custom overrides
    // The session already merges default + custom permissions
    if (currentUser && currentUser.permissions) {
      setCurrentUserPermissions(currentUser.permissions);
    } else {
      setCurrentUserPermissions(null); // Fallback: no restrictions
    }
  };

  // Fetch default permissions from backend
  const fetchDefaultPermissions = async () => {
    try {
      const permissions = await fetchDefaultPermissionsFromBackend();
      if (permissions) {
        setBackendDefaultPermissions(permissions);
      } else {
        console.warn('⚠️ Using fallback default permissions (backend unavailable)');
      }
    } catch (error) {
      console.error('Error fetching default permissions:', error);
    }
  };

  // Pre-fill form if editing
  useEffect(() => {
    if (editUser) {
      // For non-admin users, ensure they can only edit users from their own client
      if (!showClientSelection && defaultClientId && editUser.clientId !== defaultClientId) {
        showErrorMsg('You can only edit users from your own organization.');
        onClose();
        return;
      }

      reset({
        email: editUser.email || '',
        firstName: editUser.firstName || '',
        lastName: editUser.lastName || '',
        phone: editUser.phone || '',
        timezone: editUser.timezone || 'alaska',
        active: !!editUser.active,
        clientId: editUser.clientId || '',
        permissions: editUser.clientId
          ? {
              editUsers: !!editUser.permissions?.editUsers,
              editKpis: !!editUser.permissions?.editKpis,
              editPlayers: !!editUser.permissions?.editPlayers,
              editOpponents: !!editUser.permissions?.editOpponents,
              editSeasons: !!editUser.permissions?.editSeasons,
              editGames: !!editUser.permissions?.editGames,
            }
          : undefined,
        // Set role fields based on user type - backend now provides correct employeeRole and clientRoles
        clientRoles: editUser.clientRoles || [],
        employeeRole: editUser.employeeRole || '',
      });

      // Set custom permissions if they exist
      if (editUser.customPermissions) {
        setCustomPermissions(editUser.customPermissions);
        setShowAdvancedPermissions(true); // Show if custom permissions exist
      } else {
        setCustomPermissions(null);
        setShowAdvancedPermissions(false);
      }
    } else {
      // Reset custom permissions when adding new user
      setCustomPermissions(null);
      setShowAdvancedPermissions(false);
    }
  }, [editUser, reset, showClientSelection, defaultClientId, onClose]);

  // Watch for role changes and reset permissions
  useEffect(() => {
    // When role changes, reset custom permissions toggle and clear permissions
    // This ensures the user starts with default permissions for the new role
    if (watchedClientRoles && watchedClientRoles.length > 0) {
      // Get the current role being edited (if any)
      const currentEditingRole = editUser?.clientRoles?.[0];
      const selectedRole = watchedClientRoles[0];

      // If this is a NEW user, always reset
      // If EDITING, only reset if the role has changed from the original
      if (!editUser || currentEditingRole !== selectedRole) {
        setCustomPermissions(null);
        setShowAdvancedPermissions(false);
      }
    }
  }, [watchedClientRoles, editUser]);

  const fetchClients = async () => {
    try {
      const response = await api.get(API_URLS.GET_ALL_ACTIVE_CLIENTS);
      if (response.data.success && response.data.total > 0) {
        setClients(response.data.data);
        if (!editUser && response.data.data.length > 0) {
          // For users who can't select client, use provided defaultClientId or current user's client
          if (!showClientSelection) {
            if (defaultClientId) {
              setValue('clientId', defaultClientId);
            } else {
              const currentUserClientId = sessionManager.getClientId();
              if (currentUserClientId) {
                setValue('clientId', currentUserClientId);
              } else {
                setValue('clientId', response.data.data[0]._id);
              }
            }
          } else {
            setValue('clientId', response.data.data[0]._id);
          }
        } else if (editUser) {
          const match = response.data.data.find((c: any) => c._id === editUser.clientId);
          if (match) {
            setValue('clientId', match._id);
          }
        }
      }
    } catch (error) {
      showErrorMsg('Failed to fetch clients.');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get(API_URLS.GET_USER_ROLES);
      if (response.data.success && response.data.data) {
        setRoles(response.data.data);
      }
    } catch (error) {
      showErrorMsg('Failed to fetch roles.');
    }
  };

  // When editing, ensure clientId gets set once clients are available
  useEffect(() => {
    if (editUser && clients.length > 0) {
      const found = clients.find((c) => c._id === editUser.clientId);
      if (found) {
        setValue('clientId', found._id, { shouldValidate: true });
      }
    }
  }, [editUser, clients, setValue]);

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: UserFormData) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    // Determine user type based on clientId presence
    const isEmployee = !data.clientId;
    const isClient = !!data.clientId;

    // Validation based on user type
    if (isClient) {
      // Client user validation
      if (!data.clientId) {
        showErrorMsg('Client information is required for client users');
        return;
      }

      // For non-admin users, ensure they can only add/update users for their own client
      if (!showClientSelection && defaultClientId && data.clientId !== defaultClientId) {
        showErrorMsg('You can only manage users from your own organization.');
        return;
      }
    }

    // Restrict Sales and Marketing from managing employees
    if (isEmployee && !canManageEmployees) {
      showErrorMsg("You don't have permission to manage employee accounts.");
      return;
    }

    setIsLoading(true);

    try {
      let response;
      if (editUser && editUser._id) {
        // Update user
        const updateData: any = {
          ...data,
          _id: editUser._id,
          // Always include custom permissions (even if null to clear them)
          customPermissions: customPermissions,
        };
        response = await api.put(API_URLS.UPDATE_USER, updateData);
        if (response.data.success) {
          showSuccessMsg('User updated successfully!');
          onUserAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update user');
        }
      } else {
        // Invite user - backend determines type from clientId presence
        const inviteData: any = {
          ...data,
          // Include custom permissions for new users (null means use defaults)
          customPermissions: customPermissions,
        };
        response = await api.post(API_URLS.INVITE_USER, inviteData);
        if (response.data.success) {
          showSuccessMsg('User invitation sent successfully!');
          onUserAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to send user invitation');
        }
      }
    } catch (error: any) {
      console.error('Error adding/updating user:', error);

      // Handle different types of errors
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add/update user. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} maxWidth="2xl" maxHeight="90vh" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editUser ? 'Edit User' : 'Invite User'}</ModalTitle>

      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="user-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-4">User Information</h3>

              {/* Client Selection */}
              {showClientSelection && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Client (leave empty for employees)</label>
                  <select
                    {...register('clientId')}
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.clientId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading || isSubmitting}
                  >
                    {canManageEmployees && <option value="">Leave empty for employee</option>}
                    {clients.map((client: any) => (
                      <option key={client._id} value={client._id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId.message}</p>}
                </div>
              )}
              {!showClientSelection && <input type="hidden" {...register('clientId')} />}

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Email *</label>
                <input
                  type="email"
                  {...register('email')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  {...register('firstName')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  {...register('lastName')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  {...register('phone')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Time Zone *</label>
                <select
                  {...register('timezone')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.timezone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  {TIMEZONES.map((timezone) => (
                    <option key={timezone.value} value={timezone.value}>
                      {timezone.label}
                    </option>
                  ))}
                </select>
                {errors.timezone && <p className="text-red-500 text-sm mt-1">{errors.timezone.message}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  {...register('active')}
                  className="mr-2"
                  disabled={isLoading || isSubmitting}
                />
                <label htmlFor="active" className="text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div>
              {isClient ? (
                /* Client Users - Show both Permissions and Roles */
                <>
                  {/* Permissions Section */}
                  {/* <div className="mb-6">
                    <h3 className="font-semibold mb-4">Permissions</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editUsers"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-users"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label htmlFor="edit-users" className="text-gray-700">
                          Edit Users
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editKpis"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-kpis"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label htmlFor="edit-kpis" className="text-gray-700">
                          Edit KPIs
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editPlayers"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-players"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label htmlFor="edit-players" className="text-gray-700">
                          Edit Players
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editOpponents"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-opponents"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label
                          htmlFor="edit-opponents"
                          className="text-gray-700"
                        >
                          Edit Opponents
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editSeasons"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-seasons"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label htmlFor="edit-seasons" className="text-gray-700">
                          Edit Seasons
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Controller
                          name="permissions.editGames"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              id="edit-games"
                              checked={field.value}
                              onChange={field.onChange}
                              className="mr-2"
                              disabled={isLoading || isSubmitting}
                            />
                          )}
                        />
                        <label htmlFor="edit-games" className="text-gray-700">
                          Edit Games
                        </label>
                      </div>
                    </div>
                  </div> */}

                  {/* Client Role Section */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-4">Role</h3>
                    {!canEditThisUser && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> Only Head Coaches can edit another Head Coach's permissions.
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {assignableRoles.map((role) => (
                        <div key={role._id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start">
                            <Controller
                              name="clientRoles"
                              control={control}
                              render={({ field }) => {
                                const currentValues = field.value || [];
                                const isSelected = currentValues.includes(role._id);
                                return (
                                  <input
                                    type="radio"
                                    id={`client-role-${role._id}`}
                                    name="clientRole"
                                    value={role._id}
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        field.onChange([role._id]); // Single role selection
                                      }
                                    }}
                                    className="mt-1 mr-3"
                                    disabled={isLoading || isSubmitting || !canEditThisUser}
                                  />
                                );
                              }}
                            />
                            <div className="flex-1">
                              <label
                                htmlFor={`client-role-${role._id}`}
                                className={`font-medium text-gray-900 cursor-pointer ${!canEditThisUser ? 'opacity-50' : ''}`}
                              >
                                {role.label}
                              </label>
                              {ROLE_DESCRIPTIONS[role.value] && (
                                <p className="text-sm text-gray-600 mt-1">{ROLE_DESCRIPTIONS[role.value]}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Permissions Section - Show toggle for custom permissions */}
                  {canManagePermissions && watchedClientRoles && watchedClientRoles.length > 0 && canEditThisUser && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-4">Permissions</h3>

                      {/* Toggle for Custom Permissions */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-gray-700">Custom Permissions</label>
                          <p className="text-xs text-gray-500 mt-1">
                            {showAdvancedPermissions
                              ? 'Override default role permissions with custom settings'
                              : 'Using default permissions for this role'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !showAdvancedPermissions;
                            setShowAdvancedPermissions(newValue);
                            // If disabling custom permissions, clear them
                            if (!newValue) {
                              setCustomPermissions(null);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            showAdvancedPermissions ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          disabled={isLoading || isSubmitting}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              showAdvancedPermissions ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Employee Users - Show Employee Roles */
                <div className="mb-6">
                  <h3 className="font-semibold mb-4">Role</h3>
                  <div className="space-y-3">
                    {roles
                      .filter((role) => role.type === 'employee')
                      .map((role) => (
                        <div key={role._id} className="flex items-center">
                          <Controller
                            name="employeeRole"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="radio"
                                id={`employee-role-${role._id}`}
                                value={role._id}
                                checked={field.value === role._id}
                                onChange={field.onChange}
                                className="mr-2"
                                disabled={isLoading || isSubmitting}
                              />
                            )}
                          />
                          <label htmlFor={`employee-role-${role._id}`} className="text-gray-700">
                            {role.label}
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Permissions Section - For Head Coach, Assistant Coach, and Employees editing client users */}
          {canManagePermissions &&
            isClient &&
            watchedClientRoles &&
            watchedClientRoles.length > 0 &&
            showAdvancedPermissions &&
            canEditThisUser && (
              <div className="mt-6 border-t-2 border-gray-300 pt-6">
                <PermissionsEditor
                  roleValue={roles.find((r) => r._id === watchedClientRoles[0])?.value || 'statTaker'}
                  customPermissions={customPermissions}
                  onChange={(perms) => setCustomPermissions(perms)}
                  disabled={isLoading || isSubmitting}
                  backendDefaultPermissions={backendDefaultPermissions}
                  currentUserPermissions={currentUserPermissions}
                />
              </div>
            )}
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="user-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting || !canEditThisUser}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editUser ? 'Saving...' : 'Sending Invite...'}
            </>
          ) : editUser ? (
            'Save Changes'
          ) : (
            'Send Invitation'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddUserModal;
